import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

/* ── AI helpers ──────────────────────────────────────────────── */
const LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];

const getWinner  = (b) => { for (const [a,c,d] of LINES) if (b[a] && b[a]===b[c] && b[a]===b[d]) return b[a]; return null; };
const getWinLine = (b) => { for (const l of LINES) { const [a,c,d]=l; if (b[a]&&b[a]===b[c]&&b[a]===b[d]) return l; } return null; };
const getBest    = (b) => {
    const empty = b.map((v,i) => v===null ? i : -1).filter(i => i >= 0);
    for (const i of empty) { const t=[...b]; t[i]='O'; if (getWinner(t)==='O') return i; }   // win
    for (const i of empty) { const t=[...b]; t[i]='X'; if (getWinner(t)==='X') return i; }   // block
    if (empty.includes(4)) return 4;                                                           // center
    const corners = [0,2,6,8].filter(c => empty.includes(c));
    if (corners.length) return corners[Math.floor(Math.random() * corners.length)];           // corner
    return empty[Math.floor(Math.random() * empty.length)];                                   // random
};

/* ── Styled components ───────────────────────────────────────── */
const Wrap       = styled.div``;

const GameTitle  = styled.h3`
    font-size: 20px;
    letter-spacing: -0.5px;
    margin: 0 0 4px;
`;

const GameSub    = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    color: var(--color-text-meta);
    margin: 0 0 18px;
`;

const Grid       = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 43px);
`;

const Cell       = styled.button`
    width: 43px;
    height: 43px;
    background: ${({ $win }) => $win ? 'var(--color-primary-10)' : 'transparent'};
    border: none;
    border-right:  ${({ $r }) => $r ? '2px solid #eae7e4' : 'none'};
    border-bottom: ${({ $b }) => $b ? '2px solid #eae7e4' : 'none'};
    font-size: 15px;
    font-weight: 700;
    font-family: var(--font-body);
    color: ${({ $v }) => $v === 'X' ? 'var(--color-primary)' : '#999'};
    cursor: ${({ $active }) => $active ? 'pointer' : 'default'};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.2s ease;
`;

const Status     = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.textMain};
    margin: 14px 0 0;
    min-height: 20px;
`;

const PlayAgain  = styled.button`
    margin-top: var(--space-3);
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    background: var(--color-primary);
    color: #fff;
    border: none;
    border-radius: var(--radius-sm);
    padding: var(--space-2) 18px;
    cursor: pointer;
    letter-spacing: -0.01em;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.85; }
`;

/* ── Component ───────────────────────────────────────────────── */
const TicTacToe = () => {
    const [board,   setBoard]   = useState(Array(9).fill(null));
    const [xIsNext, setXIsNext] = useState(true);

    const winner  = getWinner(board);
    const isDraw  = !winner && board.every(Boolean);
    const winLine = getWinLine(board);

    // O plays after a short "thinking" delay
    useEffect(() => {
        if (xIsNext || winner || isDraw) return;
        const t = setTimeout(() => {
            const idx = getBest(board);
            if (idx == null) return;
            const next = [...board]; next[idx] = 'O';
            setBoard(next); setXIsNext(true);
        }, 480);
        return () => clearTimeout(t);
    }, [xIsNext, board, winner, isDraw]);

    const handleClick = (i) => {
        if (!xIsNext || board[i] || winner || isDraw) return;
        const next = [...board]; next[i] = 'X';
        setBoard(next); setXIsNext(false);
    };

    const reset = () => { setBoard(Array(9).fill(null)); setXIsNext(true); };

    const status = winner
        ? (winner === 'X' ? '🎉 You win!' : '🤖 I win!')
        : isDraw ? "It's a draw 🤝"
        : xIsNext ? 'Your turn' : 'Thinking...';

    return (
        <Wrap>
            <GameTitle>Quick game?</GameTitle>
            <GameSub>You're X · I'm O</GameSub>

            <Grid>
                {board.map((cell, i) => (
                    <Cell
                        key={i}
                        onClick={() => handleClick(i)}
                        $win={winLine?.includes(i)}
                        $r={i % 3 !== 2}
                        $b={i < 6}
                        $v={cell}
                        $active={!cell && xIsNext && !winner && !isDraw}
                    >
                        {cell}
                    </Cell>
                ))}
            </Grid>

            <Status>{status}</Status>
            {(winner || isDraw) && <PlayAgain onClick={reset}>Play again</PlayAgain>}
        </Wrap>
    );
};

export { TicTacToe };
